import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ApiConfiguration } from '@klubovna/api-client';
import {
  authControllerChangePassword,
  authControllerForgotPassword,
  authControllerGetMe,
  authControllerLogin,
  authControllerResetPassword,
  userControllerDeleteProfilePhoto,
  userControllerUpdatePin,
  userControllerUpdateProfile,
  userControllerUploadProfilePhoto,
} from '@klubovna/api-client';
import { AuthLoginResponseDto, MeResponseDto, UpdatePinDto, UpdateUserProfileDto, UserResponse } from '@klubovna/api-client';
import { AuthResponse, LoginRequest, User } from '@klubovna/shared-models';
import { catchError, map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly apiConfig = inject(ApiConfiguration);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly TOKEN_KEY = 'klubovna_access_token';
  private readonly USER_KEY = 'klubovna_user';
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  login(credentials: LoginRequest): Observable<AuthResponse> {
    return authControllerLogin(this.http, this.apiConfig.rootUrl, { body: credentials }).pipe(
      map((res) => res.body as AuthLoginResponseDto),
    );
  }

  validateToken(): Observable<User | null> {
    const token = this.getToken();
    if (!token) {
      return of(null);
    }

    return authControllerGetMe(this.http, this.apiConfig.rootUrl, {}).pipe(
      map((res) => res.body as MeResponseDto),
      catchError((error) => {
        if (error.status === 401) {
          this.clearAuthData();
        }
        return of(null);
      }),
    );
  }

  forgotPassword(email: string): Observable<void> {
    return authControllerForgotPassword(this.http, this.apiConfig.rootUrl, { body: { email } }).pipe(map(() => void 0));
  }

  resetPassword(token: string, password: string): Observable<void> {
    return authControllerResetPassword(this.http, this.apiConfig.rootUrl, { body: { token, password } }).pipe(
      map(() => void 0),
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<AuthResponse> {
    return authControllerChangePassword(this.http, this.apiConfig.rootUrl, { body: { currentPassword, newPassword } }).pipe(
      map((res) => res.body),
    );
  }

  updateProfile(body: UpdateUserProfileDto): Observable<UserResponse> {
    return userControllerUpdateProfile(this.http, this.apiConfig.rootUrl, { body }).pipe(
      map((res) => res.body as UserResponse),
    );
  }

  uploadProfilePhoto(file: File): Observable<UserResponse> {
    return userControllerUploadProfilePhoto(this.http, this.apiConfig.rootUrl, { body: { file } }).pipe(
      map((res) => res.body as UserResponse),
    );
  }

  deleteProfilePhoto(): Observable<UserResponse> {
    return userControllerDeleteProfilePhoto(this.http, this.apiConfig.rootUrl, {}).pipe(
      map((res) => res.body as UserResponse),
    );
  }

  updatePin(body: UpdatePinDto): Observable<UserResponse> {
    return userControllerUpdatePin(this.http, this.apiConfig.rootUrl, { body }).pipe(
      map((res) => res.body as UserResponse),
    );
  }

  setAuthData(response: AuthResponse): void {
    if (!this.isBrowser) {
      return;
    }

    localStorage.setItem(this.TOKEN_KEY, response.access_token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(response.user));
  }

  updateUserInStorage(user: User): void {
    if (!this.isBrowser) {
      return;
    }

    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  clearAuthData(): void {
    if (!this.isBrowser) {
      return;
    }

    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }

  getToken(): string | null {
    if (!this.isBrowser) {
      return null;
    }

    return localStorage.getItem(this.TOKEN_KEY);
  }

  getUserFromStorage(): User | null {
    if (!this.isBrowser) {
      return null;
    }

    const userStr = localStorage.getItem(this.USER_KEY);
    if (!userStr) return null;

    try {
      return JSON.parse(userStr) as User;
    } catch {
      this.clearAuthData();
      return null;
    }
  }

  hasStoredToken(): boolean {
    if (!this.isBrowser) {
      return false;
    }

    return !!this.getToken();
  }
}
